import React from 'react';
import { motion } from 'framer-motion';
import { MoreVertical, User } from 'lucide-react';
import { TFileItem } from '@/data/types';
import FileDropdown from './FileDropdown';

interface FileListRowProps {
  file: TFileItem;
  index: number;
  openDropdown: string | null;
  onDropdownToggle: (fileId: string) => void;
  getFileIcon: (file: TFileItem) => React.ReactNode;
  formatFileSize: (bytes: number | null | undefined) => string;
  formatDate: (dateString: string) => string;
  getFileCategory: (file: TFileItem) => string;
  canDownloadFile: (file: TFileItem) => boolean;
  onPreview: (file: TFileItem) => void;
  onDownload: (file: TFileItem) => void;
  onMove: (file: TFileItem) => void;
  onDelete: (file: TFileItem) => void;
  setAbacFile: (file: TFileItem | null) => void;
}

const FileListRow: React.FC<FileListRowProps> = React.memo(
  ({
    file,
    index,
    openDropdown,
    onDropdownToggle,
    getFileIcon,
    formatFileSize,
    formatDate,
    getFileCategory,
    canDownloadFile,
    onPreview,
    onDownload,
    onMove,
    onDelete,
    setAbacFile,
  }) => {
    return (
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3, delay: index * 0.02 }}
        draggable
        onDragStart={(e: any) => {
          e.dataTransfer.setData('text/plain', file.id);
          e.currentTarget.style.opacity = '0.5';
        }}
        onDragEnd={(e: any) => {
          e.currentTarget.style.opacity = '1';
        }}
        className="grid cursor-move grid-cols-12 gap-4 border-b border-gray-100 px-6 py-4 text-sm transition-colors hover:bg-gray-50"
      >
        <div className="col-span-5 flex items-center gap-3">
          {getFileIcon(file)}
          <div className="min-w-0 flex-1">
            <div className="truncate font-medium text-gray-900" title={file.name}>
              {file.name}
            </div>
            {file.owner && (
              <div className="flex items-center gap-1 text-xs text-blue-500">
                <User className="h-3 w-3" />
                <span>
                  {file.owner.first_name} {file.owner.last_name}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="col-span-2 flex items-center text-gray-600">{formatFileSize(file.size)}</div>
        <div className="col-span-2 flex items-center text-gray-600">{formatDate(file.created_at)}</div>
        <div className="col-span-2 flex items-center">
          <span className="inline-flex rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700">
            {getFileCategory(file)}
          </span>
        </div>
        <div className="col-span-1 flex items-center justify-end">
          <div className="relative">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={(e) => {
                e.stopPropagation();
                onDropdownToggle(file.id);
              }}
              className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              data-dropdown-trigger
            >
              <MoreVertical className="h-4 w-4" />
            </motion.button>

            {openDropdown === file.id && (
              <FileDropdown
                file={file}
                onClose={() => onDropdownToggle('')}
                canDownloadFile={canDownloadFile}
                onPreview={onPreview}
                onDownload={onDownload}
                onMove={onMove}
                onDelete={onDelete}
                onAbacEdit={() => setAbacFile(file)}
              />
            )}
          </div>
        </div>
      </motion.div>
    );
  },
);

FileListRow.displayName = 'FileListRow';

export default FileListRow;
